import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { CreateCommanWay } from "API/commonApi";
import { request } from "config/request";
import UseToast from "utils/AfToast";
import { useAuthStore } from "store/auth";

const GST_OPTIONS = [
  { value: "07AALCA0600L1ZW", label: "07AALCA0600L1ZW - Delhi", username: "Airfield_Delhi" },
  { value: "24AALCA0600L1Z0", label: "24AALCA0600L1Z0 - Gujarat", username: "Airfield_Guj" },
  { value: "27AALCA0600L1ZU", label: "27AALCA0600L1ZU - Maharashtra", username: "AIRFIELD.MAH" },
  { value: "09AALCA0600L1ZS", label: "09AALCA0600L1ZS - UP", username: "Airfield_UP" },
  { value: "29AALCA0600L1ZQ", label: "29AALCA0600L1ZQ - Karnataka", username: "Airfield_kar" },
];

const MONTHS = [
  { value: "01", label: "January" },
  { value: "02", label: "February" },
  { value: "03", label: "March" },
  { value: "04", label: "April" },
  { value: "05", label: "May" },
  { value: "06", label: "June" },
  { value: "07", label: "July" },
  { value: "08", label: "August" },
  { value: "09", label: "September" },
  { value: "10", label: "October" },
  { value: "11", label: "November" },
  { value: "12", label: "December" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const Gstr2bRecon: React.FC = () => {
  const { user } = useAuthStore();

  // OTP section state
  const [otpGstin, setOtpGstin] = useState(GST_OPTIONS[0].value);
  const [otpUsername, setOtpUsername] = useState(GST_OPTIONS[0].username);
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  const handleOtpGstinChange = (gstin: string) => {
    setOtpGstin(gstin);
    const match = GST_OPTIONS.find((g) => g.value === gstin);
    setOtpUsername(match?.username || "");
    setOtpSent(false);
    setOtpVerified(false);
    setOtp("");
  };

  // Fetch section state
  const [fetchGstin, setFetchGstin] = useState(GST_OPTIONS[0].value);
  const [fetchMonth, setFetchMonth] = useState(MONTHS[new Date().getMonth()].value);
  const [fetchYear, setFetchYear] = useState(currentYear.toString());

  // Reconcile section state
  const [reconGstin, setReconGstin] = useState(GST_OPTIONS[0].value);
  const [reconFromDate, setReconFromDate] = useState("");
  const [reconToDate, setReconToDate] = useState("");

  // Request OTP mutation
  const requestOtpMutation = useMutation(
    () =>
      CreateCommanWay({
        url: "/admin/gstr2b/request-otp",
        data: { gstin: otpGstin, username: otpUsername },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("OTP sent successfully", "success");
          setOtpSent(true);
        } else {
          UseToast(data.message || "Failed to send OTP", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err || "Failed to send OTP", "error");
      },
    }
  );

  // Verify OTP mutation
  const verifyOtpMutation = useMutation(
    () =>
      CreateCommanWay({
        url: "/admin/gstr2b/verify-otp",
        data: { gstin: otpGstin, username: otpUsername, otp },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast("OTP verified, session cached", "success");
          setOtpVerified(true);
        } else {
          UseToast(data.message || "OTP verification failed", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err || "OTP verification failed", "error");
      },
    }
  );

  // Fetch GSTR-2B mutation
  const fetchGstr2bMutation = useMutation(
    () =>
      CreateCommanWay({
        url: "/admin/gstr2b/fetch",
        data: { gstin: fetchGstin, year: fetchYear, month: fetchMonth },
      }),
    {
      onSuccess: (data: any) => {
        if (data.status === "SUCCESS") {
          UseToast(
            `GSTR-2B fetched & saved: ${data.data?.invoiceCount ?? ""} invoices`,
            "success"
          );
        } else {
          UseToast(data.message || "Failed to fetch GSTR-2B", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err || "Failed to fetch GSTR-2B", "error");
      },
    }
  );

  // Reconcile mutation — returns Excel file download
  const reconcileMutation = useMutation(
    () => {
      return request({
        url: "/admin/gstr2b/reconcile",
        method: "POST",
        body: { gstin: reconGstin, fromDate: reconFromDate, toDate: reconToDate },
        responseType: "blob",
      });
    },
    {
      onSuccess: (res: any) => {
        try {
          const fileName = `GSTR2B_Reconciliation_${reconFromDate}_to_${reconToDate}.xlsx`;

          const blob = new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
          });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(url);

          UseToast("Reconciliation file downloaded", "success");
        } catch {
          UseToast("Failed to download file", "error");
        }
      },
      onError: (err: any) => {
        UseToast(err || "Reconciliation failed", "error");
      },
    }
  );

  const labelClass = "block text-sm font-medium text-gray-700 mb-1";
  const inputClass =
    "w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500";
  const btnPrimary =
    "w-full bg-blue-600 text-white text-sm font-medium py-2 px-4 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed";
  const btnSuccess =
    "w-full bg-green-600 text-white text-sm font-medium py-2 px-4 rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed";

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto">
      <h1 className="text-xl font-semibold text-gray-800 mb-6">
        GSTR-2B Reconciliation
      </h1>

      <div className="grid grid-cols-2 md:grid-cols-1 gap-6">
        {/* LEFT — OTP Section */}
        <div className="bg-white rounded-lg shadow p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
            OTP Authentication
          </h2>

          {/* GSTIN */}
          <div>
            <label className={labelClass}>GSTIN</label>
            <select
              className={inputClass}
              value={otpGstin}
              onChange={(e) => handleOtpGstinChange(e.target.value)}
            >
              {GST_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {/* Username */}
          <div>
            <label className={labelClass}>GST Portal Username</label>
            <input
              className={inputClass}
              type="text"
              placeholder="Enter username"
              value={otpUsername}
              onChange={(e) => setOtpUsername(e.target.value)}
            />
          </div>

          {/* Request OTP */}
          <button
            className={btnPrimary}
            disabled={!otpUsername || requestOtpMutation.isLoading}
            onClick={() => requestOtpMutation.mutate()}
          >
            {requestOtpMutation.isLoading ? "Sending…" : "Request OTP"}
          </button>

          {/* OTP Input + Verify (shown after OTP sent) */}
          {otpSent && (
            <>
              <div>
                <label className={labelClass}>OTP</label>
                <input
                  className={inputClass}
                  type="text"
                  placeholder="Enter OTP"
                  maxLength={8}
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                />
              </div>
              <button
                className={btnSuccess}
                disabled={!otp || verifyOtpMutation.isLoading}
                onClick={() => verifyOtpMutation.mutate()}
              >
                {verifyOtpMutation.isLoading ? "Verifying…" : "Verify OTP"}
              </button>
            </>
          )}

          {otpVerified && (
            <p className="text-green-600 text-sm font-medium">
              ✓ Session active — you can now fetch GSTR-2B data.
            </p>
          )}
        </div>

        {/* RIGHT — Fetch GSTR-2B Section */}
        <div className="bg-white rounded-lg shadow p-5 space-y-4">
          <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
            Fetch GSTR-2B
          </h2>

          {/* GSTIN */}
          <div>
            <label className={labelClass}>GSTIN</label>
            <select
              className={inputClass}
              value={fetchGstin}
              onChange={(e) => setFetchGstin(e.target.value)}
            >
              {GST_OPTIONS.map((g) => (
                <option key={g.value} value={g.value}>
                  {g.label}
                </option>
              ))}
            </select>
          </div>

          {/* Month */}
          <div>
            <label className={labelClass}>Month</label>
            <select
              className={inputClass}
              value={fetchMonth}
              onChange={(e) => setFetchMonth(e.target.value)}
            >
              {MONTHS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label className={labelClass}>Year</label>
            <select
              className={inputClass}
              value={fetchYear}
              onChange={(e) => setFetchYear(e.target.value)}
            >
              {YEARS.map((y) => (
                <option key={y} value={y.toString()}>
                  {y}
                </option>
              ))}
            </select>
          </div>

          {/* Fetch Button */}
          <button
            className={btnPrimary}
            disabled={fetchGstr2bMutation.isLoading}
            onClick={() => fetchGstr2bMutation.mutate()}
          >
            {fetchGstr2bMutation.isLoading
              ? "Fetching…"
              : "Fetch & Save GSTR-2B"}
          </button>

          {fetchGstr2bMutation.isSuccess && (
            <p className="text-green-600 text-sm font-medium">
              ✓ GSTR-2B data saved to database.
            </p>
          )}
        </div>
      </div>

      {/* RECONCILE Section */}
      <div className="mt-6 bg-white rounded-lg shadow p-5 space-y-4 max-w-md">
        <h2 className="text-base font-semibold text-gray-700 border-b pb-2">
          Reconcile
        </h2>

        {/* GSTIN */}
        <div>
          <label className={labelClass}>GSTIN</label>
          <select
            className={inputClass}
            value={reconGstin}
            onChange={(e) => setReconGstin(e.target.value)}
          >
            {GST_OPTIONS.map((g) => (
              <option key={g.value} value={g.value}>
                {g.label}
              </option>
            ))}
          </select>
        </div>

        {/* From Date */}
        <div>
          <label className={labelClass}>From Date</label>
          <input
            className={inputClass}
            type="date"
            value={reconFromDate}
            onChange={(e) => setReconFromDate(e.target.value)}
          />
        </div>

        {/* To Date */}
        <div>
          <label className={labelClass}>To Date</label>
          <input
            className={inputClass}
            type="date"
            value={reconToDate}
            onChange={(e) => setReconToDate(e.target.value)}
          />
        </div>

        {/* Reconcile Button */}
        <button
          className={btnPrimary}
          disabled={
            reconcileMutation.isLoading ||
            !reconFromDate ||
            !reconToDate
          }
          onClick={() => reconcileMutation.mutate()}
        >
          {reconcileMutation.isLoading ? "Reconciling…" : "Reconcile"}
        </button>

        {reconcileMutation.isSuccess && (
          <p className="text-green-600 text-sm font-medium">
            ✓ Reconciliation complete.
          </p>
        )}
      </div>
    </div>
  );
};

export default Gstr2bRecon;
